import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const API_URL = "https://app.vivasmartcare.com/api";

  // Token'ı kontrol et ve kullanıcı bilgilerini yükle
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // HttpOnly cookie'ler JavaScript'ten okunamaz, direkt backend'e istek yap
      // Cookie otomatik olarak credentials: "include" ile gönderilir
      const response = await fetch(`${API_URL}/profile`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // Token geçersizse veya yoksa
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Auth kontrolü hatası:", error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/logins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password, rememberMe }),
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Login başarılı - kullanıcı bilgilerini direkt set et
        setUser(data.user);
        setIsAuthenticated(true);
        setLoading(false);
        
        return { success: true, user: data.user };
      } else {
        setLoading(false);
        return { 
          success: false, 
          error: data.message || "Giriş başarısız oldu." 
        };
      }
    } catch (error) {
      console.error("Login hatası:", error);
      setLoading(false);
      return { 
        success: false, 
        error: "Bağlantı hatası. Lütfen tekrar deneyin." 
      };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout hatası:", error);
    } finally {
      // HttpOnly cookie backend tarafından temizlendi
      setUser(null);
      setIsAuthenticated(false);
      // Navigate işlemi component tarafından yapılacak
      window.location.href = "/login";
    }
  };

  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_URL}/refresh-token`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        return { success: true };
      } else {
        // Token yenilenemezse logout yap
        await logout();
        return { success: false };
      }
    } catch (error) {
      console.error("Token refresh hatası:", error);
      await logout();
      return { success: false };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

